import React from 'react';

import {
  TpHoverInfo,
  TpHoverRenderContext,
  TpHoverRenderInfo,
  TpRectangleSelection,
  ZoomPanCanvas,
} from '../../zoompan-canvas/ZoomPanCanvas';
import { TpZoomPanelInvPointMapInfo } from '../../zoompan-canvas/zoomPanCanvasHelpers';
import { TpChartProps } from '../interfaces';
import { createDefinition } from './correlogram.Renderer';
import { convertColumnDataToDisplayString } from '../../helpers/misc';

export function Correlogram({ chartTypeManifest, dataContext, dataBinding, config, widgetContext }: TpChartProps) {
  const widgetGlobalConfig = widgetContext.dashboardContext.widgetGlobalConfig ?? {};
  const {
    definition,
    areaMatrix,
    dataTable,
    dataRowItemIds,
    dataHoverLabel,
    dataHoverLabelcolumn,
    matrixColumns,
    matrixColumnsData,
    colSortIndex,
    colSortInvIndex,
    getItemIndexById,
    hSep,
    globalMinVal,
    globalMaxVal,
    customHoverRenderer,
    colInfoHover,
    title,
  } = createDefinition(chartTypeManifest, dataContext, dataBinding, config, widgetGlobalConfig);

  const rowCount = dataRowItemIds.length;
  const colCount = matrixColumns.length;

  function getHoverInfo(
    mappingInfo: TpZoomPanelInvPointMapInfo,
    logicPositX: number,
    logicPositY: number,
    renderPositX: number,
    renderPositY: number,
  ): TpHoverInfo | null {
    if (logicPositX < 0 || logicPositX >= 1 || logicPositY < 0 || logicPositY >= 1) return null;
    const colIdxX = Math.floor(logicPositX * colCount);
    const colIdxY = Math.floor(logicPositY * colCount);
    if (colIdxY < colIdxX) return null;
    const colIdX = matrixColumns[colSortIndex[colIdxX]].id;
    const colIdY = matrixColumns[colSortIndex[colIdxY]].id;

    const columnDataX = matrixColumnsData[colIdX];
    const columnDataY = matrixColumnsData[colIdY];
    let minDist = mappingInfo.mapsize(10);
    let selItemId = null;
    let selLogicX = 0;
    let selLogicY = 0;
    for (let pointIdx = 0; pointIdx < rowCount; pointIdx++) {
      const xVal = columnDataX[pointIdx];
      const yVal = columnDataY[pointIdx];
      if (!isNaN(xVal) && !isNaN(yVal)) {
        const mX =
          (colIdxX + hSep + ((1 - 2 * hSep) * (xVal - globalMinVal)) / (globalMaxVal - globalMinVal)) / colCount;
        const mY =
          (colIdxY + 1 - hSep - ((1 - 2 * hSep) * (yVal - globalMinVal)) / (globalMaxVal - globalMinVal)) / colCount;
        const renderX = mappingInfo.mapX.map(mX);
        const renderY = mappingInfo.mapY.map(mY);
        const dst = Math.abs(renderX - renderPositX) + Math.abs(renderY - renderPositY);
        if (dst <= minDist) {
          selItemId = dataRowItemIds[pointIdx];
          selLogicX = mX;
          selLogicY = mY;
          minDist = dst;
        }
      }
    }
    if (selItemId == null) return null;
    return {
      dataTableId: dataBinding.dataTableId,
      itemId: selItemId,
      custom: {
        logicX: selLogicX,
        logicY: selLogicY,
        colIdxX,
        colIdxY,
        colIdX: matrixColumns[colSortIndex[colIdxX]].id,
        colIdY: matrixColumns[colSortIndex[colIdxY]].id,
      },
    };
  }

  function getHoverRenderInfo(hoverInfo: TpHoverInfo, context: TpHoverRenderContext): TpHoverRenderInfo | null {
    if (hoverInfo.dataTableId != dataBinding.dataTableId || !hoverInfo.custom) return null;
    if (hoverInfo.itemId == null) return null;
    const hoverItemNr = getItemIndexById(hoverInfo.itemId);
    if (hoverItemNr === undefined) return null;

    const labels: string[] = [`${hoverInfo.custom!.colIdX} - ${hoverInfo.custom!.colIdY}`];
    if (dataHoverLabel) labels.push(convertColumnDataToDisplayString(colInfoHover, dataHoverLabel[hoverItemNr]));

    return {
      area: areaMatrix,
      logicPosX: hoverInfo.custom.logicX,
      logicPosY: hoverInfo.custom.logicY,
      labels,
      customHoverRenderer,
    };
  }

  function onRectangleSelection(rectangleSelection: TpRectangleSelection) {}

  function onItemClicked(itemId: string) {
    widgetContext.handleOpenItem(dataTable.canonicalTypeId, itemId);
  }

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ZoomPanCanvas
        definition={definition}
        getHoverInfo={getHoverInfo}
        getHoverRenderInfo={getHoverRenderInfo}
        onRectangleSelection={onRectangleSelection}
        onItemClicked={onItemClicked}
        maxZoomXAbsolute={colCount * 400}
        maxZoomYAbsolute={colCount * 400}
        widgetContext={widgetContext}
        chartTitleInfo={{
          assetId: dataContext.assetId,
          dataType: chartTypeManifest.id,
          title,
        }}
      />
    </div>
  );
}
